import {
  AfterViewInit,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  QueryList,
  SimpleChanges,
  ViewChildren,
} from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { AuthFacade } from '@piqet-angular/data';
import { OtpEventData } from '@piqet-angular/shared';
import { AuthenticationService } from 'libs/data/src/lib/services/auth/authentication.service';
import { ToastrService } from 'ngx-toastr';

@Component({
  selector: 'piqet-angular-verify-otp',
  templateUrl: './verify-otp.component.html',
  styleUrls: ['./verify-otp.component.css'],
})
export class VerifyOtpComponent implements OnChanges, OnInit, AfterViewInit {
  @ViewChildren('otpInput') otpInputs!: QueryList<any>;
  @Input() userEmail!: string;
  otpForm: FormGroup;
  otpControls: FormControl[] = [];
  timerDisplay: string = '05:00';
  private timerInterval: any;
  timerCounter: number = 5;
  private intervalId?: ReturnType<typeof setInterval>;
  isExpired: boolean = false;
  @Output() verifyOtp = new EventEmitter<OtpEventData>();
  constructor(
    public authFacade: AuthFacade,
    private authService: AuthenticationService,
    private toaster: ToastrService
  ) {
    for (let i = 0; i < 6; i++) {
      this.otpControls.push(
        new FormControl('', [
          Validators.required,
          Validators.maxLength(1),
          Validators.pattern('[0-9]'),
        ])
      );
    }

    // Create form group with individual controls
    const group: any = {};
    this.otpControls.forEach((control, index) => {
      group[`digit${index}`] = control;
    });
    this.otpForm = new FormGroup(group);
  }
  ngOnChanges(changes: SimpleChanges): void {
    const userEmail = changes['userEmail'].currentValue;
    this.userEmail = this.maskEmail(userEmail);
  }

  handlePaste(event: ClipboardEvent, index: number) {
    event.preventDefault();
    const clipboardData = event.clipboardData?.getData('text') || '';
    const pastedData = clipboardData.trim().replace(/\D/g, '');

    if (pastedData) {
      const digits = pastedData.slice(0, 6).split('');

      digits.forEach((digit, i) => {
        const controlIndex = (index + i) % 6;
        this.otpControls[controlIndex].setValue(digit);
      });

      const nextEmptyIndex = this.otpControls.findIndex(
        (control, i) => i > index && !control.value
      );
      const targetInput = document.querySelector(
        `input.otp-input:nth-child(${nextEmptyIndex !== -1 ? nextEmptyIndex + 1 : 6})`
      ) as HTMLInputElement;

      if (targetInput) {
        targetInput.focus();
      }
    }
  }

  ngOnInit() {
    this.startTimer(this.timerCounter);
  }

  ngOnDestroy() {
    if (this.timerInterval) {
      clearInterval(this.timerInterval);
    }
  }

  onKeyUp(event: KeyboardEvent, index: number) {
    const input = event.target as HTMLInputElement;
    const nextInput = this.getNextInput(index);
    const prevInput = this.getPrevInput(index);

    if (input.value.length === 1 && nextInput) {
      nextInput.focus();
    } else if (event.key === 'Backspace' && prevInput) {
      prevInput.focus();
    }
  }
  maskEmail(email: string): string {
    const [local, domain] = email.split('@');
    return `${local.slice(0, 2)}***@${domain}`;
  }
  onKeyDown(event: KeyboardEvent, index: number) {
    const input = event.target as HTMLInputElement;
    if (event.key === 'Backspace' && !input.value && index > 0) {
      event.preventDefault();
      const prevInput = this.getPrevInput(index);
      if (prevInput) {
        prevInput.focus();
        prevInput.value = '';
        this.otpControls[index - 1].setValue('');
      }
    }
  }

  private getNextInput(index: number): HTMLInputElement | null {
    if (index < 5) {
      return document.querySelector(`input.otp-input:nth-child(${index + 2})`) as HTMLInputElement;
    }
    return null;
  }

  private getPrevInput(index: number): HTMLInputElement | null {
    if (index > 0) {
      return document.querySelector(`input.otp-input:nth-child(${index})`) as HTMLInputElement;
    }
    return null;
  }

  private startTimer(minutes: number) {
    let totalSeconds = minutes * 60;
    this.isExpired = false;
    this.intervalId = setInterval(() => {
      const minutes = Math.floor(totalSeconds / 60);
      const seconds = totalSeconds % 60;

      this.timerDisplay = `${this.pad(minutes)}:${this.pad(seconds)}`;

      if (--totalSeconds < 0) {
        this.stopCountdown();
        this.isExpired = true;
        this.otpForm.reset();
      }
    }, 1000);
  }
  private pad(num: number): string {
    return num.toString().padStart(2, '0');
  }

  private stopCountdown() {
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = undefined;
    }
  }

  handleResend(event: Event) {
    event.preventDefault();
    // Reset form
    this.otpForm.reset();
    // Restart timer
    if (this.timerInterval) {
      clearInterval(this.timerInterval);
    }
    this.startTimer(this.timerCounter);
  }

  onSubmit(getUserId: any) {
    if (this.otpForm.valid) {
      const otp = this.otpControls.map(control => control.value).join('');
      this.authFacade.clearVerifyError();
      this.verifyOtp.emit({ otp: Number(otp), userId: getUserId });
    }
  }
  ngAfterViewInit() {
    const firstInput = this.otpInputs.first.nativeElement;
    firstInput.focus();
  }
  backToLogin() {
    this.authFacade.clearVerifyError();
    this.authFacade.showOtpPage(false);
  }

  resendOTP(getUserId: any) {
    // Clear existing timer
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = undefined;
    }

    // Reset timer display and form
    this.timerDisplay = '05:00';
    this.otpForm.reset();
    this.isExpired = false;
    this.startTimer(this.timerCounter);
    this.authFacade.clearVerifyError();
    this.authService.resendOtp(getUserId).subscribe((res: any) => {
      this.toaster.success(res.message);
    });
  }
}
